import { action, makeAutoObservable, observable, runInAction } from 'mobx';

import { GQL } from '../../../../../../../gql/client';
import {
  Finish3dSecureCardInfo,
  FixTemporallyCheckupReservationForIndividualMutation,
  FixTemporallyCheckupReservationForIndividualMutationVariables,
  GetTemporallyTreatmentQuery,
  GetTemporallyTreatmentQueryVariables,
  Maybe,
  ShouldConfirmQuery,
  ShouldConfirmQueryVariables,
} from '../../../../../../../gql/gql-types';
import { fixTemporallyCheckupReservationForIndividual } from '../../../../../../../gql/operations/fixTemporallyCheckupReservationForIndividual';
import { getTemporallyTreatment } from '../../../../../../../gql/operations/getTemporallyTreatment';
import { shouldConfirm } from '../../../../../../../gql/operations/shouldConfirm';
import { HandledError } from '../../../../../../components/ErrorDialog';
import { Routes } from '../../../../../../constants/Routes';
import { Card } from '../../../../../../domains/customer/Card';
import { CreditCardStore } from '../../../../store/CreditCardStore';
import { ErrorInfo, Name } from '../../../stores/ProfileStore';

type Clinic = {
  id: string;
  name: string;
  termsUrl?: Maybe<string>;
};

export class CheckupReservationTemporallyStore {
  constructor() {
    makeAutoObservable(this, {
      clinic: observable,
      fetch: action,
      confirmRequired: observable,
      name: observable,
      setName: action,
      valid: observable,
      nameRequired: observable,
      setNameRequired: action,
      errors: observable,
      mail: observable,
      setMail: action,
      card: observable,
      setCard: action,
      phone: observable,
      setPhone: action,
    });
  }

  public clinic?: Clinic = undefined;
  public confirmRequired?: boolean | null = undefined;
  public nameRequired?: boolean = undefined;
  public name?: Name = undefined;
  public mail?: string = undefined;
  public id?: string = undefined;
  public departmentId?: string = undefined;
  public treatmentKindId?: string = undefined;
  public card?: Card = undefined;
  public phone?: string = undefined;

  public async fetch(id: string) {
    const treatment = await GQL.queryAsGuest<GetTemporallyTreatmentQueryVariables, GetTemporallyTreatmentQuery>(
      getTemporallyTreatment,
      { id },
    );

    if (!treatment.getTemporallyTreatment) {
      throw new HandledError(
        '原因は15分以内に予約が完了されなかったためと考えられます。お手数ですが、再度はじめから予約をお願い致します。',
      );
    }

    this.confirmRequired = (
      await GQL.query<ShouldConfirmQueryVariables, ShouldConfirmQuery>(shouldConfirm, {
        input: {
          clinic: treatment.getTemporallyTreatment.clinic?.id || '00',
        },
      })
    ).shouldConfirm?.confirmRequired;

    this.clinic = treatment.getTemporallyTreatment.clinic || undefined;

    this.departmentId = treatment.getTemporallyTreatment.department?.id;
    this.treatmentKindId = treatment.getTemporallyTreatment.treatmentKind?.id;

    this.id = id;
  }

  public async fix() {
    if (!this.id) {
      return undefined;
    }
    const res = await GQL.query<
      FixTemporallyCheckupReservationForIndividualMutationVariables,
      FixTemporallyCheckupReservationForIndividualMutation
    >(fixTemporallyCheckupReservationForIndividual, {
      input: {
        id: this.id,
        // eslint-disable-next-line no-irregular-whitespace
        name: this.name ? `${this.name.sei}　${this.name.mei}` : '',
        mail: this.mail,
        sei: this.name?.sei,
        mei: this.name?.mei,
        phone: this.phone || '',
        cardToken: this.card?.id,
        brand: this.card?.brand,
        expAt: this.card?.expAt,
        fourDigit: this.card?.fourDigit,
      },
    });

    return res.fixTreatmentCheckupIndividual?.id;
  }

  public setName(name: Name) {
    this.name = name;
    this.checkNameValidity();
  }

  public setMail(mail: string) {
    this.mail = mail;
  }

  public setNameRequired(required: boolean) {
    this.nameRequired = required;
    this.checkNameValidity();
  }

  public setCard(card: Card) {
    this.card = card;
  }

  public valid = false;
  public errors: ErrorInfo[] = [];

  private checkNameValidity() {
    runInAction(() => {
      if (!this.nameRequired) {
        this.valid = true;
        return;
      }
      // 名前関連のエラーをクリア
      this.errors = this.errors.filter(error => error.field !== 'sei' && error.field !== 'mei');

      if (!this.name) {
        this.errors = [...this.errors, { field: 'sei', error: '氏名を入力してください。' }];
        this.valid = false;
        return;
      }
      if (!/^([ァ-ン]|ー)+$/.test(this.name.sei)) {
        this.errors = [...this.errors, { field: 'sei', error: '氏名は全角カタカナで入力してください。' }];
      }
      if (!/^([ァ-ン]|ー)+$/.test(this.name.mei)) {
        this.errors = [...this.errors, { field: 'mei', error: '氏名は全角カタカナで入力してください。' }];
      }

      // エラーが残っていればvalidをfalseに設定
      this.valid = this.errors.length === 0;
    });
  }

  private checkPhoneValidity() {
    runInAction(() => {
      // 電話番号関連のエラーをクリア
      this.errors = this.errors.filter(error => error.field !== 'phone');

      if (!this.phone) {
        this.errors = [...this.errors, { field: 'phone', error: '電話番号を入力してください。' }];
        this.valid = false;
        return;
      }

      if (this.phone.startsWith('+81')) {
        const localNumber = this.phone.substring(3);
        if (!/^\d{9,10}$/.test(localNumber)) {
          this.errors = [...this.errors, { field: 'phone', error: '10桁または11桁で入力してください。' }];
        }
      } else {
        if (!/^\+\d{2,}\d{7,11}$/.test(this.phone)) {
          this.errors = [...this.errors, { field: 'phone', error: '電話番号の桁数を確認してください。' }];
        }
      }
      // エラーが残っていればvalidをfalseに設定
      this.valid = this.errors.length === 0;
    });
  }

  public cardStatus: CreditCardStore = new CreditCardStore();

  public setPhone(phone: string) {
    this.phone = phone;
    this.checkPhoneValidity();
  }

  /**
   * 健診(個人)の仮予約入力内容を一時保存する
   * 3Dセキュアなどの画面遷移を挟んでも復元できるようにする
   */
  public saveToSession() {
    const data = {
      id: this.id,
      clinic: this.clinic,
      confirmRequired: this.confirmRequired,
      departmentId: this.departmentId,
      treatmentKindId: this.treatmentKindId,
      name: this.name,
      nameRequired: this.nameRequired,
      mail: this.mail,
      phone: this.phone,
    };
    sessionStorage.setItem('tempCheckupReservation', JSON.stringify(data));
  }

  /**
   * 一時保存した内容を復元する
   */
  public loadFromSession() {
    const raw = sessionStorage.getItem('tempCheckupReservation');
    if (!raw) return;

    try {
      const data = JSON.parse(raw);

      this.id = data.id;
      this.clinic = data.clinic;
      this.confirmRequired = data.confirmRequired;
      this.departmentId = data.departmentId;
      this.treatmentKindId = data.treatmentKindId;

      // setter経由で復元（バリデーションを正しく再計算させる）
      if (data.name !== undefined) {
        this.setName(data.name);
      }
      if (data.nameRequired !== undefined) {
        this.setNameRequired(data.nameRequired);
      }
      if (data.mail !== undefined) {
        this.setMail(data.mail);
      }
      if (data.phone !== undefined) {
        this.setPhone(data.phone);
      }

      // 復元後にバリデーション更新
      this.checkNameValidity();
      this.checkPhoneValidity();
    } catch (e) {
      console.error('Failed to load temp checkup reservation from session:', e);
      throw e;
    }
  }

  /**
   * 登録／確定が完了したらセッションを削除する
   */
  public clearSession() {
    sessionStorage.removeItem('tempCheckupReservation');
  }

  /**
   * 3Dセキュア完了後の処理
   * 他のstoreにも同様のメソッドを実装しているが、各storeでメソッド名やフローが異なるため処理内容の統一はしていない
   */
  public async completeAfter3DSecure(params: {
    temporallyId: string | null;
    gclid: string | null;
    cardInfo?: Finish3dSecureCardInfo | null;
  }): Promise<{
    redirectPath: string;
    fixedId?: string;
    toastMessage: string;
  }> {
    try {
      if (params.cardInfo) {
        this.setCard({
          id: params.cardInfo.payjpID,
          brand: params.cardInfo.brand,
          expAt: params.cardInfo.expAt,
          fourDigit: params.cardInfo.fourDigit,
        });
      }
      // 1. 仮予約の最新状態をサーバから同期
      params.temporallyId && (await this.fetch(params.temporallyId));

      // 2. ユーザーが途中まで入力してた内容を復元
      this.loadFromSession();

      // 3. fix() で本予約を確定させる
      const fixedId = await this.fix();

      // 4. 成功時の遷移先・トースト文面を作成
      const gclidParam = params.gclid !== '' ? `?gclid=${params.gclid}` : '';
      const toastMessage = '健診予約が確定しました。';

      return {
        redirectPath: `${Routes.Paths.ReservationFirstSuccess}${gclidParam}`,
        fixedId,
        toastMessage,
      };
    } finally {
      // 成功でも失敗でもセッション上の一時データは掃除する
      this.clearSession();
    }
  }
}
